<template>
  <div>
    <b-overlay :show="getIsLoading" rounded="sm">
      <b-navbar>
        <div class="ml-4" v-if="tenants.length > 1">
          <b-form-select
            data-cy="TenantSelect"
            :value="selectedTenant"
            :options="tenantsOptions"
            @change="onTenantSelectorChange"
          ></b-form-select>
        </div>
        <b-navbar-nav class="ml-auto">
          <b-nav-item-dropdown right>
            <!-- Using 'button-content' slot -->
            <template #button-content>
              <span>{{ currentUsername }}</span>
            </template>
            <b-dropdown-item to="/me/profile">Settings</b-dropdown-item>
            <b-dropdown-item href="#" @click="logout">Logout</b-dropdown-item>
          </b-nav-item-dropdown>
        </b-navbar-nav>
      </b-navbar>
      <SideBar v-bind:dashboardList="dashboardList" />
      <div class="main-container">
        <router-view></router-view>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import { isActionAllowed } from '@/helpers/rights.js';
import SideBar from '@/components/sidebar/SideBar.vue';

export default {
  name: 'DefaultContainer',
  components: {
    SideBar
  },
  data() {
    return {
      userRights: []
    };
  },
  computed: {
    ...mapGetters('dashboard', ['dashboardList']),
    ...mapGetters('app', ['getIsLoading']),
    ...mapGetters('auth', ['currentUsername', 'currentUserRights']),
    ...mapGetters('tenants', ['tenants', 'selectedTenant']),
    canFetchTenantList() {
      return isActionAllowed(
        this.currentUserRights,
        'multi-tenancy/tenant',
        'list'
      );
    },
    tenantsOptions() {
      return this.tenants.map(tenant => ({
        value: tenant,
        text: `${tenant.name} (${tenant.group})`
      }));
    }
  },

  mounted() {
    this.fetchDashboards();
  },

  methods: {
    ...mapActions('tenants', ['FETCH_TENANT_LIST', 'FETCH_USER_TENANTS']),
    ...mapActions('alerts', [
      'SUBSCRIBE_TO_NEW_ALERTS',
      'FETCH_PENDING_ALERTS_COUNT'
    ]),
    ...mapMutations('tenants', ['SET_SELECTED_TENANT']),
    ...mapActions('tenants', ['FETCH_TENANT_LIST', 'FETCH_USER_TENANTS']),
    ...mapActions('dashboard', ['FETCH_DASHBOARDS']),
    logout() {
      this.$store.dispatch('auth/LOG_OUT', this.$kuzzle).then(() => {
        this.$router.push('/login');
      });
    },
    onTenantSelectorChange(tenant) {
      this.SET_SELECTED_TENANT(tenant);
    },
    async fetchDashboards() {
      this.FETCH_DASHBOARDS({
        kuzzle: this.$kuzzle,
        tenantIndex: this.selectedTenant.index
      }).catch(error => {
        this.$log.error(error);
        this.showErrorToast(error.message);
      });
    },

    showErrorToast(message) {
      this.$bvToast.toast(message, {
        title: 'Sorry, an error has occurred.',
        variant: 'danger',
        toaster: 'b-toaster-bottom-right',
        appendToast: true,
        dismissible: true,
        noAutoHide: true
      });
    }
  },

  watch: {
    selectedTenant: {
      immediate: true,
      async handler(val) {
        if (!val) {
          return;
        }
        this.$log.debug('Tenant set: fetching alerts and subscribing...');
        try {
          const promises = [
            this.FETCH_PENDING_ALERTS_COUNT({
              indexName: val._id,
              kuzzle: this.$kuzzle
            }),
            this.SUBSCRIBE_TO_NEW_ALERTS({
              indexName: val._id,
              kuzzle: this.$kuzzle,
              log: this.$log
            }),
            this.fetchDashboards()
          ];

          await Promise.all(promises);
        } catch (error) {
          this.$log(
            `Something went wrong while fetching the alerts count: ${error}`
          );
        }
      }
    }
  }
};
</script>

<style lang="scss">
body {
  overflow: hidden;
}

.navbar {
  background-color: $gray-100;
  border-bottom: 1px solid $gray-300;
  height: $navbar-height;
  padding-left: $sidebar-width;
}

.main-container {
  height: calc(100vh - #{$navbar-height});
  overflow-y: auto;
}
</style>
